import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import config from '../../config/config';

export function ViewSubcategories() {
    const [subcat, setSubcat] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const loadSubcategories = useCallback(async () => {
        try {
            const selectedYear = localStorage.getItem('qurbani_year') || new Date().getFullYear();
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${config.baseUrl}/api/subcategories/subcat?year=${selectedYear}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error("Failed to fetch data");
            const data = await response.json();
            setSubcat(Array.isArray(data) ? data : []);
        } catch (err) {
            setError("Failed to load subcategories.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSubcategories();
    }, [loadSubcategories]);

    // --- Pagination Logic (Applied directly to subcat) ---
    const totalPages = Math.ceil(subcat.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = subcat.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${config.baseUrl}/api/subcategories/deletesubcat/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) setSubcat(prev => prev.filter(item => item.id !== id));
        } catch (error) {
            alert("Delete failed");
        }
    };

    const StatusWrapper = ({ children }) => (
        <div className="min-h-screen bg-gray-50">
            
            <div className="ml-64 pt-24 p-8">{children}</div>
        </div>
    );

    if (loading) return <StatusWrapper><p className="animate-pulse">Loading...</p></StatusWrapper>;
    if (error) return <StatusWrapper><p className="text-red-500">{error}</p></StatusWrapper>;

    return (
       
            <div className="">
                <div className="rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    
                    {/* Header - Search Input Removed */}
                    <div className="p-6 border-b border-gray-100 ">
                        <div className="flex justify-between items-center">
                            <h1 className="text-2xl font-bold text-gray-800">Sub Category Details</h1>
                            <Link to="/subcategories/add" className="bg-indigo-800 text-white px-5 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                                +
                            </Link>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-indigo-800 text-white uppercase text-xs font-bold">
                                <tr>
                                    <th className="px-6 py-4 border-b">S.no</th>
                                    <th className="px-6 py-4 border-b">Name</th>
                                    <th className="px-6 py-4 border-b">Type</th>
                                    <th className="px-6 py-4 border-b">Price</th>
                                    <th className="px-6 py-4 border-b">Limits</th>
                                    <th className="px-6 py-4 border-b text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm">
                                {currentItems.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-gray-400">
                                            No subcategories found.
                                        </td>
                                    </tr>
                                ) : (
                                    currentItems.map((item, index) => (
                                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 text-gray-500">{indexOfFirstItem + index + 1}</td>
                                            <td className="px-6 py-4 font-semibold text-gray-800">{item.name}</td>
                                            <td className="px-6 py-4 font-medium text-blue-600 uppercase text-xs">{item.cat_name}</td>
                                            <td className="px-6 py-4 font-bold text-green-700">Rs. {item.price}</td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-1">
                                                    {[item.day1_limit, item.day2_limit, item.day3_limit].map((limit, i) => (
                                                        <span key={i} className="bg-gray-100 px-1.5 py-0.5 rounded text-[10px]">{limit}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex justify-center gap-4">
                                                    <Link to={`/subcategories/edit/${item.id}`} className="text-blue-600 font-medium">Edit</Link>
                                                    <button onClick={() => handleDelete(item.id)} className="text-red-600 font-medium">Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {subcat.length > 0 && (
                        <div className="p-4 border-t flex items-center justify-between bg-gray-50">
                            <span className="text-xs text-gray-500">
                                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, subcat.length)} of {subcat.length}
                            </span>
                            <div className="flex gap-1">
                                <button 
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 border rounded bg-white disabled:opacity-50 text-xs hover:bg-gray-50"
                                >
                                    Prev
                                </button>
                                {[...Array(totalPages)].map((_, i) => (
                                    <button
                                        key={i + 1}
                                        onClick={() => paginate(i + 1)}
                                        className={`px-3 py-1 border rounded text-xs transition-colors ${currentPage === i + 1 ? 'bg-indigo-800 text-white' : 'bg-white hover:bg-gray-50'}`}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button 
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 border rounded bg-white disabled:opacity-50 text-xs hover:bg-gray-50"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
  
    );
}